import { Avatar, Box, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { fetchFromApi } from "../helpers/fetchFromApi";

const present = (value) => value !== null && value !== undefined && value !== "";

export const playerAge = (dateOfBirth, now = new Date()) => {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return null;
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

export const normalizeSquadPlayer = (entry) => {
  const player = entry?.player || {};
  const position = entry?.position || player?.position || {};
  const nationality = player?.nationality || entry?.nationality || {};
  return {
    id: Number(player?.id ?? entry?.player_id ?? entry?.id),
    teamId: Number(entry?.team_id),
    name: player?.display_name || player?.common_name || player?.name || entry?.player_name || "–",
    image: player?.image_path || entry?.image_path || null,
    position: position?.name || position?.code || entry?.position_name || "–",
    number: entry?.jersey_number ?? player?.jersey_number ?? null,
    dateOfBirth: player?.date_of_birth || null,
    age: playerAge(player?.date_of_birth),
    nationality: nationality?.name || player?.nationality_name || null,
    height: player?.height ?? null,
    weight: player?.weight ?? null,
  };
};

export const normalizeFixtureLineups = (fixture) => {
  const lineups = fixture?.lineups || fixture?.data?.lineups || [];
  return (Array.isArray(lineups) ? lineups : []).map(normalizeSquadPlayer);
};

const readJson = async (path) => {
  const response = await fetchFromApi(path);
  const payload = await response.json();
  if (!response.ok || !Object.prototype.hasOwnProperty.call(payload || {}, "data")) throw new Error("Kaderdaten konnten nicht geladen werden.");
  return payload.data;
};

export async function loadFixtureSquads({ seasonId, fixtureId, homeTeamId, awayTeamId }) {
  const include = "include=player;position;player.nationality";
  const [home, away, fixture] = await Promise.all([
    readJson(`fetch-data-v3/squads/seasons/${seasonId}/teams/${homeTeamId}?${include}`),
    readJson(`fetch-data-v3/squads/seasons/${seasonId}/teams/${awayTeamId}?${include}`),
    readJson(`fetch-data-v3/fixtures/${fixtureId}?include=lineups.player;lineups.position;lineups.player.nationality`),
  ]);
  return {
    squads: {
      home: (Array.isArray(home) ? home : []).map(normalizeSquadPlayer),
      away: (Array.isArray(away) ? away : []).map(normalizeSquadPlayer),
    },
    lineups: normalizeFixtureLineups(fixture),
  };
}

function PlayerList({ players, team }) {
  return <Box data-testid={`player-list-${team.side}`}><Typography variant="caption" sx={{ color: "#ffb563", fontWeight: 900 }}>{team.name}</Typography><Box sx={{ display: "grid", gridTemplateColumns: "28px minmax(110px,1fr) 42px 30px 34px minmax(70px,.7fr) 48px 48px", gap: 0.45, px: 0.5, py: 0.3, color: "rgba(235,241,247,.45)", fontSize: "0.65rem" }}><span /><span>Name</span><span>Pos</span><span>#</span><span>Alter</span><span>Nationalität</span><span>Größe</span><span>Gewicht</span></Box>{players.map((player, index) => <Box key={Number.isFinite(player.id) ? player.id : `${player.name}-${index}`} data-testid="squad-player" sx={{ display: "grid", gridTemplateColumns: "28px minmax(110px,1fr) 42px 30px 34px minmax(70px,.7fr) 48px 48px", gap: 0.45, alignItems: "center", px: 0.5, py: 0.28, borderTop: "1px solid rgba(255,255,255,.07)", fontSize: "0.68rem" }}><Avatar src={player.image || undefined} alt="" sx={{ width: 23, height: 23, bgcolor: "rgba(255,255,255,.09)", fontSize: "0.6rem" }}>{!player.image && player.name?.charAt(0)}</Avatar><Typography variant="caption" noWrap title={player.name}>{player.name}</Typography><span>{player.position || "–"}</span><span>{present(player.number) ? player.number : "–"}</span><span title={player.dateOfBirth || ""}>{present(player.age) ? player.age : "–"}</span><Typography variant="caption" noWrap>{player.nationality || "–"}</Typography><span>{present(player.height) ? `${player.height} cm` : "–"}</span><span>{present(player.weight) ? `${player.weight} kg` : "–"}</span></Box>)}{!players.length && <Typography variant="caption" sx={{ color: "rgba(235,241,247,.55)" }}>Keine Daten verfügbar.</Typography>}</Box>;
}

export function FixtureSquads({ open, seasonId, fixtureId, teams, loader = loadFixtureSquads }) {
  const [state, setState] = useState({ status: "idle", squads: { home: [], away: [] }, lineups: [] });
  const [view, setView] = useState("squad");
  const homeTeamId = teams[0].id;
  const awayTeamId = teams[1].id;
  useEffect(() => {
    if (!open || state.status !== "idle") return undefined;
    let cancelled = false;
    setState((current) => ({ ...current, status: "loading" }));
    loader({ seasonId, fixtureId, homeTeamId, awayTeamId })
      .then((data) => { if (!cancelled) setState({ status: "ready", squads: data.squads, lineups: data.lineups }); })
      .catch(() => { if (!cancelled) setState({ status: "error", squads: { home: [], away: [] }, lineups: [] }); });
    return () => { cancelled = true; };
    // status is intentionally excluded: the loading transition must not cancel its own request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awayTeamId, fixtureId, homeTeamId, loader, open, seasonId]);
  const lineupBySide = useMemo(() => ({ home: state.lineups.filter((player) => Number(player.teamId) === Number(homeTeamId)), away: state.lineups.filter((player) => Number(player.teamId) === Number(awayTeamId)) }), [awayTeamId, homeTeamId, state.lineups]);
  if (state.status === "loading") return <Stack direction="row" gap={1} alignItems="center"><CircularProgress size={18} /><Typography variant="caption">Kader werden geladen …</Typography></Stack>;
  if (state.status === "error") return <Typography variant="caption" color="error.light">Kaderdaten sind derzeit nicht verfügbar. Der übrige Arbeitsbereich bleibt unverändert nutzbar.</Typography>;
  if (state.status === "idle") return null;
  const hasLineup = lineupBySide.home.length > 0 || lineupBySide.away.length > 0;
  const displayed = view === "lineup" ? lineupBySide : state.squads;
  return <Box data-testid="fixture-squads"><Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>{hasLineup ? <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, next) => next && setView(next)}><ToggleButton value="squad">Kader</ToggleButton><ToggleButton value="lineup">Aufstellung</ToggleButton></ToggleButtonGroup> : <Typography variant="caption" sx={{ color: "rgba(235,241,247,.55)" }}>Noch keine Aufstellung verfügbar.</Typography>}</Box><Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 0.9, overflowX: "auto" }}>{teams.map((team) => <PlayerList key={team.side} team={team} players={displayed[team.side] || []} />)}</Box></Box>;
}
